import { ReactElement } from "react";

import { SelectControl } from "@/Common/Components/Inputs/SelectControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { TextInput } from "@/DesignSystem";
import { CreateFlowsheetTemplateScope } from "@/Flowsheets/Api/Contracts/CreateFlowsheetTemplate";

import styles from "./TemplateTypeSelectionStep.module.scss";

interface ITemplateTypeSelectionStepProps {
    /**
     * The name of the template.
     */
    templateName: string;

    /**
     * Callback function triggered when the template name changes.
     * @param templateName - The updated template name.
     */
    onTemplateNameChange: (templateName: string) => void;

    /**
     * Error message to display.
     */
    errorMessage: string | null;

    /**
     * Callback function triggered when a template scope is selected.
     * @param templateType - The selected template type.
     */
    onTemplateScopeSelected: (templateType: CreateFlowsheetTemplateScope) => void;

    /**
     * The selected template scope.
     */
    templateScope: CreateFlowsheetTemplateScope | null;

    /**
     * Indicates whether the template name control is disabled.
     */
    isTemplateNameControlDisabled: boolean;
}

function TemplateTypeSelectionStep(props: Readonly<ITemplateTypeSelectionStepProps>): ReactElement {
    const { getText } = useTranslation();

    const options = [
        {
            label: getText("Flowsheets_TemplateOption_PatientTemplateLabel"),
            value: CreateFlowsheetTemplateScope.Patient
        },
        {
            label: getText("Flowsheets_TemplateOption_PhysicianTemplateLabel"),
            value: CreateFlowsheetTemplateScope.Physician
        },
        {
            label: getText("Flowsheets_TemplateOption_ClinicTemplateLabel"),
            value: CreateFlowsheetTemplateScope.Clinic
        }
    ];

    return (
        <div className={styles.templateTypeSelectionContainer} data-cy="flowsheets-template-type-selection-modal">
            <p>{getText("Flowsheets_SelectFlowsheetOptionLabel")}</p>
            <SelectControl
                options={options}
                value={props.templateScope}
                onChange={option => props.onTemplateScopeSelected(option)}
            />
            {props.templateScope && (
                <>
                    <p className={styles.templateTypeDescription}>{getText(`Flowsheets_TemplateOption_${CreateFlowsheetTemplateScope[props.templateScope]}TemplateDescription`)}</p>
                    <div className={styles.templateNameContainer}>
                        <p>{getText("Flowsheets_FlowsheetNameLabel")}</p>
                        <TextInput
                            dataCy="flowsheets-configuration-template-name-input"
                            key={props.templateScope}
                            value={props.templateName}
                            onChange={event => props.onTemplateNameChange(event.target.value)}
                            disabled={props.isTemplateNameControlDisabled}
                            errorMessage={props.errorMessage}
                            hasError={!!props.errorMessage && props.templateScope !== CreateFlowsheetTemplateScope.Patient}
                            maxLength={200}
                            autoFocus
                        />
                    </div>
                </>
            )}
        </div>
    );
}

export default TemplateTypeSelectionStep;
